import { Module } from 'vuex';
import axios, { AxiosResponse } from 'axios';
import {
  SET_EMPLOYEES,
  SET_EMPLOYEE,
  SET_ERRORMSG,
  SET_EMPLOYEE_STATS,
  SET_EMPLOYEE_DIRECTORY,
  SET_EMPLOYEE_ACTIVITY,
} from '../mutation_types';
import {
  REGISTER_EMPLOYEE,
  GET_EMPLOYEE,
  GET_EMPLOYEES,
  CLEAR_EMPLOYEES_ISLOADING,
  EDIT_EMPLOYEE,
  TOGGLE_EMPLOYEE_IS_ACTIVE,
  DELETE_EMPLOYEE,
  GET_EMPLOYEE_STATS,
  GET_EMPLOYEE_DIRECTORY,
  GET_EMPLOYEE_ACTIVITY,
  INVITE_EMPLOYEE,
  UPLOAD_EMPLOYEE_PROFILE_PHOTO,
} from '../action_types';
import { StateObject } from '@/dtos/state.dto';
import Employee from '@/services/employee';
import { EditEmployee, GetEmployee, GetEmployeeActivity, GetEmployeeDirectory, RegisterEmployee } from '@/dtos/employee.dto';

// Only the latest activity request may write to the store, so a slow page of a previous
// filter or employee never replaces the one on screen.
let activityRequest = 0;

const employee: Module<any, {}> = {
  state: {
    employee: {
      data: null,
      isLoading: false,
    },
    employeeStats: {
      data: null,
      isLoading: false,
    },
    employees: {
      data: null,
      metaData: null,
      isLoading: false,
    },
    actingEmployee: {
      data: null,
      metaData: null,
      isLoading: false,
    },
    employeeDirectory: {
      data: null,
      metaData: null,
      isLoading: false,
    },
    employeeActivity: {
      data: null,
      metaData: null,
      /** whose trail `data` holds */
      employeeId: null,
      isLoading: false,
    },
  },
  getters: {
    employeeDirectory: (state: { employeeDirectory: any }) => state.employeeDirectory,
    employeeActivity: (state: { employeeActivity: any }) => state.employeeActivity,
    employee: (state: { employee: any }) => state.employee,
    employeeStats: (state: { employeeStats: any }) => state.employeeStats,
    actingEmployee: (state: { actingEmployee: any }) => state.actingEmployee,
    employees: (state: { employees: any }) => state.employees,
  },
  mutations: {
    [SET_EMPLOYEE]: (
      state: { employee: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.employee.data = payload.data) : null,
        (state.employee.isLoading = payload.isLoading);
    },
    [SET_EMPLOYEE_STATS]: (
      state: { employeeStats: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.employeeStats.data = payload.data) : null,
        (state.employeeStats.isLoading = payload.isLoading);
    },
    [SET_EMPLOYEES]: (
      state: { employees: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      payload.data != null ? (state.employees.data = payload.data) : null,
        payload.metaData != null
          ? (state.employees.metaData = payload.metaData)
          : null,
        (state.employees.isLoading = payload.isLoading);
    },
    [SET_EMPLOYEE_DIRECTORY]: (
      state: { employeeDirectory: StateObject },
      payload: { data?: any; metaData?: any; isLoading: boolean }
    ) => {
      if (payload.data !== undefined) state.employeeDirectory.data = payload.data;
      if (payload.metaData !== undefined) state.employeeDirectory.metaData = payload.metaData;
      state.employeeDirectory.isLoading = payload.isLoading;
    },
    [SET_EMPLOYEE_ACTIVITY]: (
      state: { employeeActivity: StateObject & { employeeId: string | null } },
      payload: { data?: any; metaData?: any; employeeId?: string; isLoading: boolean }
    ) => {
      if (payload.data !== undefined) state.employeeActivity.data = payload.data;
      if (payload.metaData !== undefined) state.employeeActivity.metaData = payload.metaData;
      if (payload.employeeId !== undefined) state.employeeActivity.employeeId = payload.employeeId;
      state.employeeActivity.isLoading = payload.isLoading;
    },
  },
  actions: {
    [GET_EMPLOYEE_DIRECTORY]: async ({ commit }: any, payload: GetEmployeeDirectory) => {
      // keep the previous page visible while the next one loads
      commit(SET_EMPLOYEE_DIRECTORY, { isLoading: true });

      const result: AxiosResponse = await Employee.getEmployeeDirectory(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_EMPLOYEE_DIRECTORY, { data: result.data.data, metaData: result.data.metaData, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE_DIRECTORY, { isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_EMPLOYEE_DIRECTORY, { isLoading: false });
      return false;
    },
    [GET_EMPLOYEE_ACTIVITY]: async ({ commit, state }: any, payload: GetEmployeeActivity) => {
      const request = ++activityRequest;
      // keep the previous page visible while the next one loads, unless it is another employee's
      if (state.employeeActivity.employeeId === payload.id) commit(SET_EMPLOYEE_ACTIVITY, { isLoading: true });
      else commit(SET_EMPLOYEE_ACTIVITY, { data: null, metaData: null, employeeId: payload.id, isLoading: true });

      const result: AxiosResponse = await Employee.getEmployeeActivity(payload);
      if (request !== activityRequest) return false;

      if (result) {
        if (result.data.status) {
          commit(SET_EMPLOYEE_ACTIVITY, { data: result.data.data, metaData: result.data.metaData, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE_ACTIVITY, { isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_EMPLOYEE_ACTIVITY, { isLoading: false });
      return false;
    },
    [REGISTER_EMPLOYEE]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE, { data: null, isLoading: true });

      let result: AxiosResponse = await Employee.registerEmployee(
        payload as RegisterEmployee
      );

      if (result) {
        if (result.data.status) {
          commit(SET_EMPLOYEE, { data: result.data.data, isLoading: false });
          return result.data.data;
        }
        commit(SET_EMPLOYEE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_EMPLOYEE, { isLoading: false });
        return false;
      }
    },
    [GET_EMPLOYEE]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE, { data: null, isLoading: true });

      let result: AxiosResponse = await Employee.getEmployees(payload as GetEmployee);

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data[0]);
          commit(SET_EMPLOYEE, { data: result.data.data[0], isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_EMPLOYEE, { isLoading: false });
        return false;
      }
    },
    [GET_EMPLOYEES]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEES, { data: null, isLoading: true });

      let result: AxiosResponse = await Employee.getEmployees(payload as GetEmployee);

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_EMPLOYEES, {
            data: result.data.data,
            metaData: result.data.metaData,
            isLoading: false,
          });
          return true;
        }
        commit(SET_EMPLOYEES, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_EMPLOYEES, { isLoading: false });
        return false;
      }
    },
    [GET_EMPLOYEE_STATS]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE_STATS, { data: null, isLoading: true });

      let result: AxiosResponse = await Employee.getEmployeeStats(payload as GetEmployee);

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_EMPLOYEE_STATS, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE_STATS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_EMPLOYEE_STATS, { isLoading: false });
        return false;
      }
    },
    [EDIT_EMPLOYEE]: async ({ commit }: any, payload: any) => {
      commit(SET_EMPLOYEE, { data: null, isLoading: true });

      let result: AxiosResponse = await Employee.editEmployee(payload as EditEmployee);

      if (result) {
        if (result.data.status) {
          commit(SET_EMPLOYEE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_EMPLOYEE, { isLoading: false });
        return false;
      }
    },
    [INVITE_EMPLOYEE]: async ({ commit }: any, payload: string) => {
      commit(SET_EMPLOYEE, { data: null, isLoading: true });

      let result: AxiosResponse = await Employee.inviteEmployee(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_EMPLOYEE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_EMPLOYEE, { isLoading: false });
        return false;
      }
    },
    [TOGGLE_EMPLOYEE_IS_ACTIVE]: async (
      { commit, state }: any,
      payload: EditEmployee
    ) => {
      commit(SET_EMPLOYEE, { isLoading: true });

      let result: AxiosResponse = await Employee.toggleEmployeeIsActive(
        payload as EditEmployee
      );

      if (result) {
        if (result.data.status) {
          let index;
          if (state.employees.data) {
            state.employees.data.forEach((e: any, i: number) => {
              if (e.id == result.data.data.id) {
                index = i;
              }
            });
            if (index != null)
              state.employees.data[index].isActive = result.data.data.isActive;
          }

          commit(SET_EMPLOYEE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_EMPLOYEE, { isLoading: false });
        return false;
      }
    },
    [DELETE_EMPLOYEE]: async ({ commit }: any, payload: string) => {
      commit(SET_EMPLOYEE, { data: null, isLoading: true });

      let result: AxiosResponse = await Employee.deleteEmployee(payload);

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_EMPLOYEE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_EMPLOYEE, { isLoading: false });
        return false;
      }
    },
    [UPLOAD_EMPLOYEE_PROFILE_PHOTO]: async ({ commit }: any, payload: { employeeId: string; formData: FormData | null }) => {
      commit(SET_EMPLOYEE, { isLoading: true });

      let result: AxiosResponse = await Employee.uploadProfilePhoto(
        payload.employeeId,
        payload.formData
      );

      if (result) {
        if (result.data.status) {
          commit(SET_EMPLOYEE, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_EMPLOYEE, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_EMPLOYEE, { isLoading: false });
        return false;
      }
    },
    [CLEAR_EMPLOYEES_ISLOADING]: ({ commit }) => {
      commit(SET_EMPLOYEE, { isLoading: false });
      commit(SET_EMPLOYEES, { isLoading: false });
      commit(SET_EMPLOYEE_DIRECTORY, { isLoading: false });
      commit(SET_EMPLOYEE_ACTIVITY, { isLoading: false });
    },
  },
};

export default employee;
